import * as styles from '../components/TrailDetails/WeatherCard/styles/styles.module.scss';

const getColorClass = uvDesc => {
  switch (uvDesc) {
    case 'low':
      return styles.low;
    case 'moderate':
      return styles.moderate;
    case 'high':
      return styles.high;
    case 'very-high':
      return styles.veryHigh;
    case 'extreme':
      return styles.extreme;
    default:
      return '';
  }
};

const getNumberedOverlay = (uvIndex, key) => {
  let classes = ``;

  // to keep double digit numbers centered
  if (uvIndex > 9) {
    classes += `${styles.doubleDigitPadding} `;
  }

  classes += getColorClass(key);

  return <span className={classes}>{uvIndex}</span>;
};

// The timestamp comes in formatted like: '2021-07-21T20:22:15-0600'
// We need to preserve the time as it exists in the timestamp and not display client time
const formatLocalizedTime = (locale = 'en-US', dateString) => {
  const date = new Date(dateString.slice(0, 19)); // discard the timezone
  return new Intl.DateTimeFormat(locale, { timeStyle: 'short' }).format(date);
};

const weatherText = (tempHigh, tempLow, tempUnit) => {
  if (tempHigh && tempLow) {
    return `${tempHigh}${`\u00B0`} / ${tempLow}${`\u00B0`} ${tempUnit} `;
  }
  if (tempLow) {
    return `${tempLow}${`\u00B0`} ${tempUnit}`;
  }

  return '';
};

const generateSrcSet = baseUrl => {
  const srcSet = [];
  let src;

  for (let i = 1; i <= 3; i++) {
    src = i === 1 ? baseUrl.replace('.png', `.png ${i}x`) : baseUrl.replace('.png', `@${i}x.png ${i}x`);
    srcSet.push(src);
  }

  return srcSet;
};

const getUVKey = uvIndex => {
  // have to generate our own UV key because the translations are not consistent with our images
  switch (uvIndex) {
    case 0:
    case 1:
    case 2:
      return 'low';
    case 3:
    case 4:
    case 5:
      return 'moderate';
    case 6:
    case 7:
    case 8:
      return 'high';
    case 9:
    case 10:
      return 'very-high';
    case 11:
      return 'extreme';
    default:
      return 'extreme';
  }
};

const getDaylightProps = (locale, today, intl) => {
  if (!today?.sunrise || !today?.sunset) {
    return [];
  }

  const sunrise = formatLocalizedTime(locale, today.sunrise);
  const sunset = formatLocalizedTime(locale, today.sunset);
  const sunriseSrc = 'https://cdn-assets.alltrails.com/assets/icons/cuttlefish-weather/sun/icons-daytime-sunrise.png';
  const sunsetSrc = 'https://cdn-assets.alltrails.com/assets/icons/cuttlefish-weather/sun/icons-daytime-sunset.png';
  const sunriseSrcSet = generateSrcSet(sunriseSrc);
  const sunsetSrcSet = generateSrcSet(sunsetSrc);

  return [
    { topText: intl.formatMessage({ defaultMessage: 'Sunrise' }), bottomText: sunrise, imgSrc: sunriseSrc, srcSet: sunriseSrcSet },
    { topText: intl.formatMessage({ defaultMessage: 'Sunset' }), bottomText: sunset, imgSrc: sunsetSrc, srcSet: sunsetSrcSet }
  ];
};

const getUvIndexProp = day => {
  const dayOrNight = day?.day || day?.night;
  if (!dayOrNight) return null;

  const topText = day.dow;
  const bottomText = dayOrNight.uv_desc;
  const key = getUVKey(dayOrNight.uv_index);
  const overlay = getNumberedOverlay(dayOrNight.uv_index, key);
  const imgSrc = `https://cdn-assets.alltrails.com/assets/icons/cuttlefish-weather/uv/icons-uv-${key}.png`;
  const srcSet = generateSrcSet(imgSrc);

  return { topText, bottomText, imgSrc, srcSet, overlay };
};

const formatIconString = num => {
  if (num.length == 1) {
    return `0${num}`;
  }

  return `${num}`;
};

const getWeatherTabProp = (day, metric) => {
  const dayOrNight = day?.day || day?.night;
  if (!dayOrNight) return null;

  const iconString = formatIconString(dayOrNight.icon_code);
  const imgSrc = `https://cdn-assets.alltrails.com/assets/icons/cuttlefish-weather/conditions/icons-weather-icon-weather-${iconString}.png`;
  const srcSet = generateSrcSet(imgSrc);
  const tempUnit = metric ? 'C' : 'F';
  const topText = day.dow;
  const bottomText = weatherText(day.max_temp, day.min_temp, tempUnit);

  return { topText, bottomText, imgSrc, srcSet };
};

export { formatLocalizedTime, weatherText, getDaylightProps, getWeatherTabProp, getUvIndexProp };
